import { ChangeDetectorRef, Component, ElementRef, ViewChild } from "@angular/core";
import { ImportsModule } from "../imports";

import { ScopeService } from "../services/scope.service";
import { MessagingService } from "../services/messaging.service";
import { TreeNode } from "primeng/api";
import { Tree } from "primeng/tree";
import { debounceTime, Subject, Subscription } from "rxjs";

interface Column {
  actions?: any[];
  key?: boolean;
  label: string;
  field: string;
  width?: number;
  align?: string;
  options?: any[];
  filterValue?: any;
  maxLength?: number,
  upperCase?: boolean;
  hidden?: boolean;
  filterType?: string;
  matchMode?: string;
  editable?: boolean;
  title?: string;
  getText?: boolean;
}

@Component({
  selector: "app-entrypoints",
  standalone: true,
  imports: [ImportsModule],
  templateUrl: "./entrypoints.component.html",
  styleUrl: "./entrypoints.component.css"
})

export class EntrypointsComponent {
  @ViewChild("mt") menuTree!: Tree;
  items!: TreeNode[] | null;
  selectedProgram!: TreeNode | null;
  pgmItems: TreeNode[] = [];
  cols!: Column[];
  types: { label: string, value: any; }[] = [{ label: "Refactor", value: "Refactor" }, { label: "Rewrite", value: "Rewrite" }, { label: "Retain", value: "Retain" }, { label: "Retire", value: "Retire" }, { label: "Replace", value: "Replace" }];
  libraryNames!: any[];
  appAreaTypes!: any[];
  phaseOptions!: any[];
  hasDisplayTypes: { label: string, value: any; }[] = [{ label: "Yes", value: true }, { label: "No", value: false }];
  menuSearch: string = "";
  private lastMenuSearch: string = "";
  menuSearchTimeout!: NodeJS.Timeout;

  constructor(private scopingService: ScopeService, private messagingService: MessagingService, private cd: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.onNodeExpand();

    this.scopingService.getAppAreas({ orderby: "area" }).subscribe({
      next: (data: any) => {
        this.appAreaTypes = data.items.map((i: any) => {
          const item = { label: i.area, value: i.id };
          if (i.text) item.label += ` - ${i.text}`;
          return item;
        });

        this.scopingService.getScopePhases({ orderby: "phase" }).subscribe({
          next: (data: any) => {
            this.phaseOptions = data.items.map((i: any) => {
              const item = { label: i.description, value: i.phase };
              return item;
            });

            this.cols = [
              { field: "name", label: "Name", width: 300, maxLength: 10, upperCase: true },
              { field: "attribute", label: "Attribute", width: 175, maxLength: 10, upperCase: true },
              { field: "objTarget", label: "Target", width: 200, options: this.types, matchMode: "in", editable: true, getText: true },
              { field: "objAppArea", label: "Application", width: 275, options: this.appAreaTypes, matchMode: "in", editable: true, getText: true },
              { field: "objPhase", label: "Phase", width: 50, options: this.phaseOptions, matchMode: "in", align: "right", editable: true, getText: true },
              { field: "hasDisplay", label: "Screen", width: 200, options: this.hasDisplayTypes, matchMode: "in", getText: true },
            ];
          },
          error: (error: any) => {
            this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
          }
        });
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      }
    });
  }

  onMenuSearchChanged() {
    clearTimeout(this.menuSearchTimeout);
    this.menuSearchTimeout = setTimeout(() => {
      if (this.menuSearch === this.lastMenuSearch) return;
      this.lastMenuSearch = this.menuSearch;
      this.items = null;
      this.selectedProgram = null;
      this.filterMenu();
    }, 500);
  }

  onNodeExpand(expandingItem?: any) {
    if (expandingItem && expandingItem.node.children) {
      expandingItem.node.loading = false;
      return;
    }
    const node = expandingItem?.node;
    const filter: any = { filters: {}, orderby: "option" };

    if (!node) {
      filter.filters.level = { matchMode: "equals", value: 0 };
    } else {
      node.loading = true;
      filter.filters.root = { matchMode: "equals", value: node.data.root };
      filter.filters.level = { matchMode: "equals", value: node.data.level + 1 };
      filter.filters.path = { matchMode: "startsWith", value: node.data.path + "," };
    }

    this.getMenuData(filter, node);
  }

  getMenuData(filter: any, node?: TreeNode) {
    if (node && node.children) {
      node.loading = false;
      return;
    }
    if (node) node.loading = true;

    this.scopingService.getEntryPoints(filter).subscribe({
      next: (data: any) => {

        if (filter.searchText) {
          // First turn lots of records into a tree
          const keys: any = {};
          const allKeys: any = {};
          let root: TreeNode[] = [];
          for (const item of data.items.sort((a: any, b: any) => a.path.localeCompare(b.path))) {
            item.node = this.turnMenuItemIntoTreeData(item);
            const thisKey = item.path;
            const keyParts = item.path.split(",");
            keyParts.pop();
            const parentKey = keyParts.join(",");

            if (!item.parent) {
              if (allKeys[thisKey]) continue;
              allKeys[thisKey] = item.node;
              item.node.expanded = true;
              root.push(item.node);
              continue;
            }

            if (item.optionType === "M") {
              if (allKeys[thisKey]) continue;
              allKeys[thisKey] = item.node;
            }
            if (!allKeys[parentKey].children) allKeys[parentKey].children = [];
            allKeys[parentKey].children.push(item.node);
          }
          this.items = root;
        } else {
          const items: TreeNode[] = [];
          for (const item of data.items) {
            const treeItem = this.turnMenuItemIntoTreeData(item);
            items.push(treeItem);
          }
          if (!node) this.items = items;
          else {
            if (!items.length) node.leaf = true;
            else node.children = items;
            node.loading = false;
          }
        }

        this.cd.markForCheck();
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      },
    });
  }

  getProgramData(filter: any) {
    this.scopingService.getEntryPoints(filter).subscribe({
      next: (data: any) => {
        // First turn lots of records into a tree
        const keys: any = {};
        let root: TreeNode = {} as TreeNode;
        for (const item of data.items) {
          item.node = this.turnProgramItemIntoTreeData(item);
          if (!item.parentID) {
            root = item.node;
            root.expanded = true;
          }
          keys[item.objID] = item.node;
        }

        for (const item of data.items.sort((a: any, b: any) => a.level - b.level)) {
          if (item.parentID == null) continue;
          let obj = keys[item.parentID];
          if (!obj) continue;
          if (!obj.children) {
            obj.children = [];
            obj.leaf = false;
          }
          obj.children.push(item.node);
        }

        this.pgmItems = [root];
        this.cd.markForCheck();
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      },
    });
  }

  turnMenuItemIntoTreeData(data: any): TreeNode {
    const key = `${data.root}-${data.level}-${data.epID}`;
    const isFunction = data.optionType === "F";
    const item: TreeNode = {
      key,
      label: data.title,
      data: data,
      expandedIcon: !isFunction ? "pi pi-folder-open" : "pi pi-angle-double-right",
      collapsedIcon: !isFunction ? "pi pi-folder" : "pi pi-angle-double-right",
      leaf: isFunction,
      type: isFunction ? "function" : "menu"
    };

    return item;
  }

  turnProgramItemIntoTreeData(data: any): TreeNode {
    const key = `${data.parentID}-${data.objID}`;
    const item: TreeNode = {
      key,
      label: data.name,
      data: data,
      expandedIcon: "pi pi-folder-open",
      collapsedIcon: "pi pi-folder",
      leaf: true,
      type: "program"
    };

    return item;
  }

  navItemSelected(event: any) {
    let node = event.node;
    let newSelected = node?.data.optionType == "F" ? node : null;
    if (newSelected) {
      if (newSelected === this.selectedProgram) return;
      this.selectedProgram = newSelected;
      this.pgmItems.length = 0;
      const filter: any = {
        program: true,
        filters: {
          epID: { matchMode: "equals", value: node.data.epID }
        }
      };
      this.getProgramData(filter);
    } else {
      this.selectedProgram = null;
      this.pgmItems.length = 0;
      if (node && !node.leaf) {
        if (node.expanded) node.expanded = false;
        else {
          node.expanded = true;
          this.onNodeExpand(event);
        }
      }
    }
  }

  applyFilter(thing: any, event: any, field: string, matchMode: string) {
    if (event.itemValue) {
      thing.filter(event.value, field, matchMode);
      return;
    }
    thing.filter(event.target.value, field, matchMode);
  }

  getText(item: any, column: any) {
    const found = column.options.find((o: any) => o.value === item[column.field]);
    return found ? found.label : item[column.field];
  }

  filterMenu() {
    if (!this.menuSearch) return this.onNodeExpand();
    else {
      const filter: any = { searchText: this.menuSearch };
      this.getMenuData(filter);
    }
  }
}
